import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { GameSettings, Statistics, AchievementProgress, HandHistoryEntry, GameMode } from '../types';
import { useGameStore } from '../store/gameStore';
import { DEFAULT_SETTINGS } from '../store/settingsStore';
import { DEFAULT_STATISTICS } from '../store/statisticsStore';
import { restoreDeckFromSeed } from '../utils/deck';
import { trackRead, trackWrite, flushUsageToFirestore } from './firebaseUsageTracker';

// =============================================================================
// FIREBASE PERSISTENCE (Cross-device sync)
// Settings, stats, achievements, and high scores are synced across devices
// =============================================================================

type SettingsState = GameSettings;
type StatsState = Statistics;
type AchievementsState = {
  achievements?: AchievementProgress[];
};
type HighScoresState = {
  speedDrillHighScore?: number;
};

export type FirebaseUserState = {
  settings?: SettingsState;
  stats?: StatsState;
  achievements?: AchievementsState;
  highScores?: HighScoresState;
};

const DEBOUNCE_MS = 60000; // 60 seconds - optimized for cost (writes still flush immediately on tab exit)

type PendingSaves = {
  timer: ReturnType<typeof setTimeout> | null;
  payload: FirebaseUserState;
};

const pending: Record<string, PendingSaves> = {};

// Helper: Remove undefined values from objects (Firestore doesn't accept undefined)
function removeUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as (keyof T)[]) {
    const value = obj[key];
    if (value !== undefined) {
      // Recursively clean nested objects (but not arrays)
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[key] = removeUndefined(value as Record<string, unknown>) as T[keyof T];
      } else {
        result[key] = value;
      }
    }
  }
  return result;
}

// Current authenticated user id, set by useAuth on login/logout
let currentUid: string | null = null;

export function setCurrentUid(uid: string | null) {
  currentUid = uid;
}

export function getCurrentUid(): string | null {
  return currentUid;
}

function settingsRef(uid: string) {
  return doc(db, 'users', uid, 'state', 'settings');
}
function statsRef(uid: string) {
  return doc(db, 'users', uid, 'state', 'stats');
}
function achievementsRef(uid: string) {
  return doc(db, 'users', uid, 'state', 'achievements');
}
function highScoresRef(uid: string) {
  return doc(db, 'users', uid, 'state', 'highScores');
}

export async function loadFirebaseState(uid: string): Promise<FirebaseUserState> {
  const [settingsSnap, statsSnap, achievementsSnap, highScoresSnap] = await Promise.all([
    getDoc(settingsRef(uid)),
    getDoc(statsRef(uid)),
    getDoc(achievementsRef(uid)),
    getDoc(highScoresRef(uid)),
  ]);

  // Track reads for usage monitoring
  trackRead('users/state/settings', 'hydrate');
  trackRead('users/state/stats', 'hydrate');
  trackRead('users/state/achievements', 'hydrate');
  trackRead('users/state/highScores', 'hydrate');

  return {
    settings: settingsSnap.exists() ? (settingsSnap.data() as SettingsState) : undefined,
    stats: statsSnap.exists() ? (statsSnap.data() as StatsState) : undefined,
    achievements: achievementsSnap.exists() ? (achievementsSnap.data() as AchievementsState) : undefined,
    highScores: highScoresSnap.exists() ? (highScoresSnap.data() as HighScoresState) : undefined,
  };
}

// Load admin's protected settings (leveling, achievements, branding)
// These propagate from admin to all users they invited
// Reads from adminConfigs collection - a separate doc that invited users can access
export async function loadAdminProtectedSettings(adminUid: string): Promise<Partial<SettingsState> | null> {
  try {
    const configRef = doc(db, 'adminConfigs', adminUid);
    const configSnap = await getDoc(configRef);
    trackRead('adminConfigs', 'admin-settings');
    
    if (!configSnap.exists()) {
      return null;
    }
    
    const adminConfig = configSnap.data();
    
    // Only return protected fields that propagate to invited users
    return {
      leveling: adminConfig.leveling,
      customAchievements: adminConfig.customAchievements,
      achievementEmojiOptions: adminConfig.achievementEmojiOptions,
      casinoName: adminConfig.casinoName,
    };
  } catch (err) {
    console.warn('Failed to load admin config:', err);
    return null;
  }
}

async function writeFirebaseState(uid: string, bundle: FirebaseUserState) {
  const tasks: Promise<unknown>[] = [];
  
  // Clean undefined values before writing (Firestore rejects undefined)
  if (bundle.settings) {
    const cleanSettings = removeUndefined(bundle.settings as unknown as Record<string, unknown>);
    if (Object.keys(cleanSettings).length > 0) {
      tasks.push(setDoc(settingsRef(uid), cleanSettings, { merge: true }));
    trackWrite('users/state/settings', 'sync');
    }
  }
  if (bundle.stats) {
    const cleanStats = removeUndefined(bundle.stats as unknown as Record<string, unknown>);
    if (Object.keys(cleanStats).length > 0) {
      tasks.push(setDoc(statsRef(uid), cleanStats, { merge: true }));
    trackWrite('users/state/stats', 'sync');
    }
  }
  if (bundle.achievements) {
    const cleanAchievements = removeUndefined(bundle.achievements as unknown as Record<string, unknown>);
    if (Object.keys(cleanAchievements).length > 0) {
      tasks.push(setDoc(achievementsRef(uid), cleanAchievements, { merge: true }));
    trackWrite('users/state/achievements', 'sync');
    }
  }
  if (bundle.highScores) {
    const cleanHighScores = removeUndefined(bundle.highScores as unknown as Record<string, unknown>);
    if (Object.keys(cleanHighScores).length > 0) {
      tasks.push(setDoc(highScoresRef(uid), cleanHighScores, { merge: true }));
    trackWrite('users/state/highScores', 'sync');
    }
  }
  await Promise.all(tasks);
}

export function queueFirebaseSave(uid: string, partial: FirebaseUserState) {
  const existing = pending[uid] || { timer: null, payload: {} };
  const mergedPayload: FirebaseUserState = { ...existing.payload };
  if (partial.settings) mergedPayload.settings = { ...existing.payload?.settings, ...partial.settings };
  if (partial.stats) mergedPayload.stats = { ...existing.payload?.stats, ...partial.stats };
  if (partial.achievements) mergedPayload.achievements = { ...existing.payload?.achievements, ...partial.achievements };
  if (partial.highScores) mergedPayload.highScores = { ...existing.payload?.highScores, ...partial.highScores };

  pending[uid] = {
    timer: existing.timer,
    payload: mergedPayload,
  };

  if (existing.timer) return;

  pending[uid].timer = setTimeout(async () => {
    const bundle = pending[uid]?.payload;
    pending[uid] = { timer: null, payload: {} };
    if (bundle && Object.keys(bundle).length) {
      try {
        await writeFirebaseState(uid, bundle);
      } catch (e) {
        console.warn('Failed to save user state to Firebase', e);
      }
    }
  }, DEBOUNCE_MS);
}

// Convenience: queue save for current user
function queueSave(partial: FirebaseUserState) {
  if (!currentUid) return;
  queueFirebaseSave(currentUid, partial);
}

// Firebase save helpers (only for cross-device data)
export function queueSettingsSave(settings: GameSettings) {
  queueSave({ settings });
}

export function queueStatsSave(stats: Statistics) {
  queueSave({ stats });
}

export function queueAchievementsSave(achievements: AchievementProgress[]) {
  queueSave({ achievements: { achievements } });
}

export function queueHighScoresSave(speedDrillHighScore: number) {
  queueSave({ highScores: { speedDrillHighScore } });
}

export async function flushFirebaseState(uid: string) {
  if (pending[uid]?.timer) {
    clearTimeout(pending[uid].timer as ReturnType<typeof setTimeout>);
    pending[uid] = { timer: null, payload: {} };
  }

  const state = useGameStore.getState();
  const snapshot: FirebaseUserState = {
    settings: state.settings,
    stats: state.statistics,
    achievements: { achievements: state.achievements },
    highScores: { speedDrillHighScore: state.speedDrill.highScore },
  };
  await writeFirebaseState(uid, snapshot);
}

// =============================================================================
// LOCALSTORAGE PERSISTENCE (Device-specific session state)
// Bankroll, gameMode, deck state, hand history - survives refresh on same device
// =============================================================================

const LOCAL_SESSION_KEY = 'blackjack-trainer-session';
const LOCAL_HISTORY_KEY = 'blackjack-trainer-history';

type DeckPersistState = {
  seed: number;
  cardsDealt: number;
  runningCount: number;
};

type LocalSessionState = {
  bankroll: number;
  gameMode: GameMode;
  showTutorial: boolean;
  speedDrillHighScore?: number;
  trainingDeck?: DeckPersistState;
  tableDeck?: DeckPersistState;
};

function loadLocalStorageJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T;
  } catch {
    // ignore
  }
  return null;
}

function saveLocalStorageJSON<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore (quota exceeded, etc.)
  }
}

// Save session state to localStorage (called from gameStore subscriber)
export function saveLocalSession(): void {
  const state = useGameStore.getState();
  
  const session: LocalSessionState = {
    bankroll: state.bankroll,
    gameMode: state.gameMode,
    showTutorial: state.showTutorial,
    speedDrillHighScore: state.speedDrill.highScore,
  };

  // Only save deck state if we have a seed
  if (state.deckSeed !== null) {
    session.trainingDeck = {
      seed: state.deckSeed,
      cardsDealt: state.dealtCards.length,
      runningCount: state.runningCount,
    };
  }

  if (state.tableSimDeck.deckSeed !== null) {
    session.tableDeck = {
      seed: state.tableSimDeck.deckSeed,
      cardsDealt: state.tableSimDeck.dealtCards.length,
      runningCount: state.tableSimDeck.runningCount,
    };
  }

  saveLocalStorageJSON(LOCAL_SESSION_KEY, session);
}

// Save hand history to localStorage
export function saveLocalHistory(history: HandHistoryEntry[]): void {
  saveLocalStorageJSON(LOCAL_HISTORY_KEY, history.slice(-50));
}

// Load session state from localStorage
export function loadLocalSession(): LocalSessionState | null {
  return loadLocalStorageJSON<LocalSessionState>(LOCAL_SESSION_KEY);
}

// Load hand history from localStorage
export function loadLocalHistory(): HandHistoryEntry[] {
  return loadLocalStorageJSON<HandHistoryEntry[]>(LOCAL_HISTORY_KEY) || [];
}

// =============================================================================
// HYDRATION (Combines Firebase + localStorage on app load)
// =============================================================================

// Apply Firebase data to gameStore (settings, stats, achievements, highScores)
function applyFirebaseToGameStore(bundle: FirebaseUserState) {
  const state = useGameStore.getState();
  const updates: Partial<ReturnType<typeof useGameStore.getState>> = {};

  if (bundle.settings) {
    updates.settings = { ...DEFAULT_SETTINGS, ...bundle.settings };
  }

  if (bundle.stats) {
    updates.statistics = { ...DEFAULT_STATISTICS, ...bundle.stats };
  }

  if (bundle.achievements?.achievements) {
    updates.achievements = bundle.achievements.achievements;
  }

  // Apply high scores from Firebase (cross-device sync)
  if (bundle.highScores?.speedDrillHighScore !== undefined) {
    // Use the higher of local or remote high score
    const remoteHighScore = bundle.highScores.speedDrillHighScore;
    const localHighScore = state.speedDrill.highScore;
    const bestHighScore = Math.max(remoteHighScore, localHighScore);
    
    updates.speedDrill = {
      ...state.speedDrill,
      highScore: bestHighScore,
    };
  }

  if (Object.keys(updates).length) {
    useGameStore.setState(updates);
  }
}

// Apply localStorage session data to gameStore
function applyLocalSessionToGameStore(session: LocalSessionState) {
  const store = useGameStore.getState();
  const updates: Partial<ReturnType<typeof useGameStore.getState>> = {};

  updates.bankroll = session.bankroll;
  updates.gameMode = session.gameMode;
  updates.showTutorial = session.showTutorial;

  // Restore high score if available (use max of stored vs current)
  if (session.speedDrillHighScore !== undefined) {
    const bestHighScore = Math.max(session.speedDrillHighScore, store.speedDrill.highScore);
    updates.speedDrill = {
      ...store.speedDrill,
      highScore: bestHighScore,
    };
  }

  // Restore training deck from seed if available
  if (session.trainingDeck?.seed !== undefined) {
    const { seed, cardsDealt, runningCount } = session.trainingDeck;
    const { deck, dealtCards } = restoreDeckFromSeed(store.settings.numDecks, seed, cardsDealt);
    
    const decksRemaining = Math.max(0.5, deck.length / 52);
    const trueCount = Math.round((runningCount / decksRemaining) * 10) / 10;
    
    updates.deck = deck;
    updates.dealtCards = dealtCards;
    updates.deckSeed = seed;
    updates.runningCount = runningCount;
    updates.trueCount = trueCount;
  }

  // Restore table simulator deck from seed if available
  if (session.tableDeck?.seed !== undefined) {
    const { seed, cardsDealt, runningCount } = session.tableDeck;
    const { deck, dealtCards } = restoreDeckFromSeed(store.settings.numDecks, seed, cardsDealt);
    
    const decksRemaining = Math.max(0.5, deck.length / 52);
    const trueCount = Math.round((runningCount / decksRemaining) * 10) / 10;
    
    updates.tableSimDeck = {
      ...store.tableSimDeck,
      deck,
      dealtCards,
      deckSeed: seed,
      runningCount,
      trueCount,
    };
  }

  useGameStore.setState(updates);
}

// Main hydration function - called on app load/login
// invitedByUid is optional - if provided, admin's protected settings will be applied
export async function hydrateStoresFromUserState(uid: string, invitedByUid?: string | null): Promise<boolean> {
  // Load Firebase data (cross-device: settings, stats, achievements, highScores)
  const firebaseData = await loadFirebaseState(uid);
  const hasFirebaseData = !!(firebaseData.settings || firebaseData.stats || firebaseData.achievements || firebaseData.highScores);

  // If user was invited by an admin, load and apply admin's protected settings
  // These settings (leveling, achievements, branding) propagate from admin to invited users
  if (invitedByUid) {
    const adminSettings = await loadAdminProtectedSettings(invitedByUid);
    if (adminSettings) {
      // Merge admin's protected settings with user's settings
      // Admin settings override user's for protected fields
      if (firebaseData.settings) {
        firebaseData.settings = {
          ...firebaseData.settings,
          ...adminSettings,
        };
      } else {
        firebaseData.settings = adminSettings as SettingsState;
      }
    }
  }

  // Apply Firebase data first (settings needed for deck restoration)
  if (hasFirebaseData || firebaseData.settings) {
    applyFirebaseToGameStore(firebaseData);
  }

  // Load localStorage session (device-specific: bankroll, deck, gameMode, highScore)
  const localSession = loadLocalSession();
  if (localSession) {
    applyLocalSessionToGameStore(localSession);
  }

  // Load localStorage history
  const localHistory = loadLocalHistory();
  if (localHistory.length) {
    useGameStore.setState({ handHistory: localHistory });
  }

  return hasFirebaseData;
}

// Initialize local session on first load (before Firebase hydration)
export function initializeLocalSession(): void {
  const localSession = loadLocalSession();
  if (localSession) {
    applyLocalSessionToGameStore(localSession);
  }

  const localHistory = loadLocalHistory();
  if (localHistory.length) {
    useGameStore.setState({ handHistory: localHistory });
  }
}

// =============================================================================
// LEGACY: Old function names for backwards compatibility during migration
// =============================================================================

// Kept for backwards compatibility - now only saves Firebase data
export async function flushUserState(uid: string) {
  await flushFirebaseState(uid);
  saveLocalSession(); // Also save local session on flush
  // Also flush usage tracking (piggyback on the same flush)
  flushUsageToFirestore();
}

// Snapshot for Firebase (only cross-device data)
export function snapshotUserState(): FirebaseUserState {
  const state = useGameStore.getState();
  return {
    settings: state.settings,
    stats: state.statistics,
    achievements: { achievements: state.achievements },
    highScores: { speedDrillHighScore: state.speedDrill.highScore },
  };
}
